import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/toast';
import { useTranslation } from 'react-i18next';
import useAuthContext from './useAuthContext';
import { GetInvitationService } from '../services/profile/profileService';

const rawDomain = import.meta.env.VITE_FRONT_DOMAIN || '';
const frontDomain = rawDomain.endsWith('/') ? rawDomain : rawDomain + '/';

/**
 * Hook que encapsula la lógica de obtención y copia del enlace de invitación.
 * Construye el mensaje de invitación completo (con el texto introductorio)
 * y lo copia al portapapeles.
 *
 * @returns {{ code: string|undefined, isModalOpen: boolean, copyInviteLink: Function, closeModal: Function }}
 */
export default function useInviteLink() {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuthContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data } = useQuery(
    ['user-invitation'],
    () => GetInvitationService({ token: user?.access_token }),
    {
      enabled: !!user?.access_token && !user?.invitation_code,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const code = user?.invitation_code || data?.data?.data;

  const copyInviteLink = () => {
    if (!code) {
      toast({
        title: t('referral.copyNotReady'),
        description: t('referral.copyNotReadyDescription'),
        status: 'info',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return false;
    }

    const link = frontDomain + 'register/' + code;
    const message = t('referral.inviteMessage', { link });

    window.navigator.clipboard.writeText(message);
    toast({
      title: t('referral.copySuccess'),
      description: t('referral.copySuccessDescription'),
      status: 'success',
      duration: 2000,
      isClosable: true,
      position: 'top-right',
    });
    setIsModalOpen(true);
    return true;
  };

  const closeModal = () => setIsModalOpen(false);

  return { code, isModalOpen, copyInviteLink, closeModal };
}
